import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMosque } from 'react-icons/fa6';
import { IoArrowBack } from 'react-icons/io5';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { publicRequest } from '../../requestMethods';
import MosqueLoader from '../../utils/MosqueLoader';

// import { LOCATIONS } from '../../utils/Locations';
import './AddMosqueForm.scss';
import { LOCATIONS } from '../../utils/Locations';
import LocationFilter from '../LocationFilter/LocationFilter';
import DishFilter from '../DishFilter/DishFilter';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer';

const AddMosqueForm = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        country: 'Kerala',
        district: '',
        state: 'Kerala',
        customCountry: '',
        customDistrict: '',
        dish: '',
        googleMapsLink: '',
        location: {
            latitude: '',
            longitude: ''
        },
        description: '',
        keywords: ''
    });

    // Used to decide whether to mark mosque as active or pending on backend
    const [isLikelyMosque, setIsLikelyMosque] = useState(null); // true | false | null (unknown)

    const [locationError, setLocationError] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchingLocationDetails, setFetchingLocationDetails] = useState(false);
    // 'checking' | 'mosque' | 'not_mosque' | null (not checked yet)
    const [mosqueCheckStatus, setMosqueCheckStatus] = useState(null);


    const handleCountryChange = (value) => {
        const loc = LOCATIONS[value];
        setFormData(prev => ({
            ...prev,
            country: value,
            district: '',
            state: value === 'India' ? '' : (loc?.name || value),
            customCountry: '',
            customDistrict: ''
        }));
    };

    const handleStateChange = (value) => {
        setFormData(prev => ({ ...prev, state: value, district: '' }));
    };

    const handleDistrictChange = (value) => {
        setFormData(prev => ({ ...prev, district: value }));
    };

    const handleDishChange = (value) => {
        setFormData(prev => ({ ...prev, dish: value }));
    };

    const extractCoordsFromGoogleMaps = (url) => {
        try {
            if (!url?.trim()) return null;
            const regex = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
            const match = url.match(regex);
            if (match) {
                const [, latitude, longitude] = match;
                return { latitude: parseFloat(latitude), longitude: parseFloat(longitude) };
            }
            const placeRegex = /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/;
            const placeMatch = url.match(placeRegex);
            if (placeMatch) {
                const [, latitude, longitude] = placeMatch;
                return { latitude: parseFloat(latitude), longitude: parseFloat(longitude) };
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    const isShortOrResolvableMapUrl = (url) => {
        if (!url?.trim()) return false;
        return url.includes('maps.app.goo.gl') || url.includes('goo.gl/maps') || url.includes('g.co/kgs/') ||
            (url.includes('google.com/maps') && url.startsWith('http'));
    };

    // Fetch country, district and a suggested mosque name from coordinates (called on blur of Google Maps link).
    // Also calls backend to check OpenStreetMap (Overpass) whether this location is a mosque.
    const fetchLocationDetails = async (latitude, longitude) => {
        setFetchingLocationDetails(true);
        setMosqueCheckStatus('checking');
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`
            );
            const data = await response.json();
            const address = data.address || {};

            // Try to infer which "country" key in LOCATIONS to use
            let detectedCountryKey = null;

            Object.entries(LOCATIONS).forEach(([key, value]) => {
                if (value.isOther) return;
                if (value.name === address.country && !detectedCountryKey) {
                    detectedCountryKey = key;
                }
            });

            if (!detectedCountryKey && address.country === 'India') {
                detectedCountryKey = 'India';
            }
            if (!detectedCountryKey && (address.state === 'Kerala' || address.state_district === 'Kerala')) {
                detectedCountryKey = 'Kerala';
            }

            const detectedDistrict =
                address.county ||
                address.state_district ||
                address.city ||
                address.town ||
                address.village ||
                '';
            const detectedState = address.state || '';

            let suggestedName = data.name || address.mosque || '';
            if (!suggestedName && data.display_name) {
                suggestedName = data.display_name.split(',')[0].trim();
            }

            // Check if detected district exists in the country's list
            const countryLoc = detectedCountryKey ? LOCATIONS[detectedCountryKey] : null;
            let districtInList = false;
            let useOther = false;
            if (countryLoc?.hasStates && countryLoc?.states) {
                const stateCities = detectedState ? (countryLoc.states[detectedState] || []) : [];
                districtInList = stateCities.includes(detectedDistrict);
                useOther = !detectedState || !districtInList;
            } else {
                districtInList = countryLoc?.districts?.includes(detectedDistrict);
                useOther = !detectedCountryKey || (detectedCountryKey && !districtInList && detectedDistrict);
            }

            if (countryLoc?.hasStates && countryLoc?.states && detectedState && !useOther) {
                setFormData(prev => ({
                    ...prev,
                    country: detectedCountryKey || prev.country,
                    state: detectedState,
                    district: detectedDistrict || prev.district,
                    customCountry: '',
                    customDistrict: '',
                    name: suggestedName || prev.name
                }));
            } else if (useOther) {
                const isIndiaOther = detectedCountryKey === 'India';
                const isCountryWithDistricts = detectedCountryKey && !countryLoc?.hasStates;
                setFormData(prev => ({
                    ...prev,
                    country: detectedCountryKey || 'Other',
                    district: isIndiaOther || isCountryWithDistricts ? 'Other (specify below)' : '',
                    state: isIndiaOther ? (detectedState || prev.state) : prev.state,
                    customCountry: !detectedCountryKey ? (address.country || address.state || '') : '',
                    customDistrict: (!detectedCountryKey || isIndiaOther || isCountryWithDistricts) ? (detectedDistrict || prev.customDistrict) : prev.customDistrict,
                    name: suggestedName || prev.name
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    country: detectedCountryKey || prev.country,
                    district: detectedDistrict || prev.district,
                    state: countryLoc?.hasStates ? prev.state : (LOCATIONS[detectedCountryKey]?.name || prev.state),
                    customCountry: '',
                    customDistrict: '',
                    name: suggestedName || prev.name
                }));
            }

            // Server-side mosque check (Overpass/OSM) – more reliable than name heuristic
            try {
                const validateRes = await publicRequest.get('/mosques/validate-location', {
                    params: { lat: latitude, lon: longitude }
                });
                if (validateRes.data?.success === true) {
                    const isMosque = !!validateRes.data.isMosque;
                    setIsLikelyMosque(isMosque);
                    setMosqueCheckStatus(isMosque ? 'mosque' : 'not_mosque');
                } else {
                    setMosqueCheckStatus('not_mosque');
                    setIsLikelyMosque(false);
                }
            } catch (apiErr) {
                console.error('Mosque validation check failed:', apiErr);
                setMosqueCheckStatus('not_mosque');
                setIsLikelyMosque(false);
            }
        } catch (error) {
            console.error('Error fetching location details:', error);
            setMosqueCheckStatus(null);
        } finally {
            setFetchingLocationDetails(false);
        }
    };

    const handleGoogleMapsLinkBlur = () => {
        const { latitude, longitude } = formData.location || {};
        if (latitude != null && longitude != null && !fetchingLocationDetails) {
            fetchLocationDetails(latitude, longitude);
        }
    };

    const resolveMapUrlViaApi = async (url) => {
        setFetchingLocationDetails(true);
        setLocationError('');
        try {
            const res = await publicRequest.post('/mosques/resolve-map-url', { googleMapsLink: url });
            if (res.data?.success && res.data?.coordinates) {
                const { latitude, longitude } = res.data.coordinates;
                setFormData(prev => ({ ...prev, googleMapsLink: url, location: { latitude, longitude } }));
                await fetchLocationDetails(latitude, longitude);
            } else {
                setLocationError(res.data?.message || 'Could not extract location from this link.');
            }
        } catch (err) {
            setLocationError(err.response?.data?.message || 'Could not resolve map link. Try the full Google Maps URL.');
        } finally {
            setFetchingLocationDetails(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setLocationError(''); // Clear any previous errors

        if (name === 'googleMapsLink') {
            const coords = extractCoordsFromGoogleMaps(value);
            setMosqueCheckStatus(null);
            if (coords) {
                setFormData(prev => ({
                    ...prev,
                    googleMapsLink: value,
                    location: coords
                }));
            } else if (isShortOrResolvableMapUrl(value)) {
                setFormData(prev => ({ ...prev, googleMapsLink: value }));
                resolveMapUrlViaApi(value);
            } else {
                setFormData(prev => ({
                    ...prev,
                    googleMapsLink: value
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleGetLocation = () => {
        setLocationError(''); // Clear any previous errors
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    // Generate and set Google Maps link
                    const mapsLink = `https://www.google.com/maps?q=${latitude},${longitude}`;
                    setFormData(prev => ({
                        ...prev,
                        location: { latitude, longitude },
                        googleMapsLink: mapsLink
                    }));
                },
                (error) => {
                    let errorMessage = 'Error getting location';
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = 'Please allow location access to use this feature';
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = 'Location information unavailable';
                            break;
                        case error.TIMEOUT:
                            errorMessage = 'Location request timed out';
                            break;
                        default:
                            errorMessage = 'An unknown error occurred';
                    }
                    setLocationError(errorMessage);
                    console.error('Error getting location:', error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0
                }
            );
        } else {
            setLocationError('Geolocation is not supported by your browser');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Resolve actual district and state for API
        let district = formData.district;
        let state = formData.state;
        if (formData.country === 'Other') {
            district = formData.customDistrict?.trim();
            state = formData.customCountry?.trim();
            if (!district || !state) {
                snackbar.error('Please enter both Country/Region and District/City for locations outside the list.');
                return;
            }
        } else if (formData.country === 'India') {
            state = formData.state?.trim();
            district = formData.district === 'Other (specify below)' ? formData.customDistrict?.trim() : formData.district;
            if (!state || !district) {
                snackbar.error('Please select state and city for India.');
                return;
            }
        } else if (formData.district === 'Other (specify below)') {
            district = formData.customDistrict?.trim();
            state = LOCATIONS[formData.country]?.name || formData.country;
            if (!district) {
                snackbar.error('Please enter the district or city name.');
                return;
            }
        } else {
            state = LOCATIONS[formData.country]?.name || formData.country;
        }

        if (!district || !formData.dish) {
            snackbar.error('Please select district and food type!');
            return;
        }

        console.log("user", user);
        setLoading(true);
        // Convert keywords from string to array
        const formattedKeywords = (formData.keywords || '').split(',').map(k => k.trim()).filter(Boolean);
        const requestData = {
            ...formData,
            district,
            state,
            keywords: formattedKeywords,
            // Pass hint to backend so it can decide pending/active status
            isLikelyMosque,
            contributor: {
                name: user.name,
                email: user.email,
                picture: user.picture,
                sub: user.sub
            }
        };

        try {
            const response = await publicRequest.post("/mosques/add-mosque", requestData);
            console.log(response);
            snackbar.success('Mosque added successfully!');

            // Clear form data
            setFormData({
                name: '',
                country: 'Kerala',
                district: '',
                state: 'Kerala',
                customCountry: '',
                customDistrict: '',
                dish: '',
                googleMapsLink: '',
                location: { latitude: '', longitude: '' },
                description: '',
                keywords: ''
            });
        } catch (error) {
            console.error('Error submitting form:', error);
            const msg = error.response?.data?.message || error.response?.data?.error || 'Error submitting form!';
            snackbar.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const hasValidLink = formData.location?.latitude != null && formData.location?.longitude != null;

    return (
        <>
            <Navbar />
            <div className={`add-mosque ${!hasValidLink ? 'add-mosque--step1-only' : ''}`}>
                {loading && <MosqueLoader />}
                <div className="add-mosque__header">
                    <button
                        className="add-mosque__back-btn"
                        onClick={() => navigate('/')}
                        aria-label="Go back"
                    >
                        <IoArrowBack />
                    </button>
                    <FaMosque className="add-mosque__icon" />
                    <h1 className="add-mosque__title">Add New Mosque</h1>
                </div>

                <form className="add-mosque__form" onSubmit={handleSubmit}>

                    {/* 1. Google Maps link first – only step shown until link is pasted */}
                    <div className="form-group form-group--location add-mosque__link-step">
                        <div className="form-group">
                            <label htmlFor="googleMapsLink">
                                Google Maps Link
                            </label>
                            <input
                                type="url"
                                id="googleMapsLink"
                                name="googleMapsLink"
                                value={formData.googleMapsLink}
                                onChange={handleChange}
                                onBlur={handleGoogleMapsLinkBlur}
                                placeholder="Paste Google Maps link of the mosque"
                                required
                                disabled={fetchingLocationDetails}
                            />
                        </div>
                        {fetchingLocationDetails && (
                            <div className="form-group__loader form-group__loader--inline">
                                <span className="form-group__loader-spinner" aria-hidden="true" />
                                <span>Fetching location details…</span>
                            </div>
                        )}
                        {!fetchingLocationDetails && mosqueCheckStatus === 'mosque' && (
                            <div className="form-group__hint form-group__hint--success" role="status">
                                ✓ Mosque detected – listing will be active
                            </div>
                        )}
                        {!fetchingLocationDetails && mosqueCheckStatus === 'not_mosque' && (
                            <div className="form-group__hint form-group__hint--warning" role="status">
                                ⚠ Not verified as mosque – listing will be pending review
                            </div>
                        )}
                        {locationError && (
                            <div className="form-group__error">{locationError}</div>
                        )}
                    </div>

                    {/* Rest of form – revealed after valid link is pasted */}
                    {hasValidLink && (
                    <>
                    {/* Mosque filters – Location and Food Type (all areas) */}
                    <div className="add-mosque__filters">
                        <div className="form-group">
                            <label>Location</label>
                            <LocationFilter
                                selectedCountry={formData.country || 'Kerala'}
                                selectedDistrict={formData.district}
                                selectedState={formData.country === 'India' ? formData.state : ''}
                                onCountryChange={handleCountryChange}
                                onDistrictChange={handleDistrictChange}
                                onStateChange={handleStateChange}
                                customCountry={formData.customCountry}
                                customDistrict={formData.customDistrict}
                                onCustomCountryChange={(v) => setFormData(prev => ({ ...prev, customCountry: v }))}
                                onCustomDistrictChange={(v) => setFormData(prev => ({ ...prev, customDistrict: v }))}
                            />
                        </div>
                        <div className="form-group">
                            <label>Food Type</label>
                            <DishFilter
                                selectedDish={formData.dish}
                                onDishChange={handleDishChange}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="name">Mosque Name</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Enter mosque name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Additional details about the mosque and food service"
                            rows="3"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="keywords">Keywords</label>
                        <input
                            type="text"
                            id="keywords"
                            name="keywords"
                            value={formData.keywords}
                            onChange={handleChange}
                            placeholder="Comma-separated keywords"
                        />
                    </div>

                    <button type="submit" className="submit-btn">
                        Add Mosque
                    </button>
                    </>
                    )}
                </form>
            </div>
            <Footer />
        </>
    );
};

export default AddMosqueForm; 